import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { User, Phone, MapPin, Truck, Recycle, ArrowRight, Check, XCircle, Coins, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  ["RECEIVED", "Received"], ["PAYMENT_VERIFIED", "Payment Verified"], ["CONFIRMED", "Confirmed"],
  ["PREPARING", "Preparing"], ["READY", "Ready"], ["OUT_FOR_DELIVERY", "Out for Delivery"], ["DELIVERED", "Delivered"],
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [staff, setStaff] = useState([]);
  const load = () => api.get("/admin/orders/manage").then(({ data }) => setOrders(data));
  useEffect(() => { load(); api.get("/admin/staff").then(({ data }) => setStaff(data)); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  return (
    <AdminLayout title="Order Management">
      <div className="space-y-5" data-testid="orders-manage">
        {orders.length === 0 && <div className="text-muted-foreground text-center py-10 bg-card border border-border rounded-lg">No orders yet.</div>}
        {orders.map((o) => <OrderCard key={o.order_id} o={o} staff={staff} act={act} />)}
      </div>
    </AdminLayout>
  );
}

function OrderCard({ o, staff, act }) {
  const cancelled = o.fulfillment_status === "CANCELLED";
  const refunded = o.payment_status === "refunded";
  const curIdx = STAGES.findIndex(([k]) => k === o.fulfillment_status);
  const deliveryStaff = staff.filter((s) => s.role === "staff");

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`order-card-${o.order_id}`}>
      {/* NEXT ACTION banner */}
      <div className={`px-5 py-3 flex flex-wrap items-center justify-between gap-2 ${cancelled || refunded ? "bg-muted text-foreground" : o.next_status ? "bg-primary text-primary-foreground" : "bg-[hsl(var(--secondary))] text-white"}`}>
        <div className="flex items-center gap-2 font-heading font-bold text-lg" data-testid={`next-action-${o.order_id}`}>
          {o.next_status ? <AlertCircle size={18} /> : cancelled || refunded ? <XCircle size={18} /> : <Check size={18} />}
          {o.next_status ? "NEXT ACTION: " : "STATUS: "}{o.next_action}
        </div>
        <div className="text-sm font-mono-points opacity-90">#{o.order_id}</div>
      </div>

      <div className="p-5">
        {/* stepper */}
        <div className="flex items-center gap-1 overflow-x-auto pb-3">
          {STAGES.map(([k, label], i) => {
            const done = !cancelled && curIdx >= 0 && i <= curIdx;
            return (
              <React.Fragment key={k}>
                <div className={`whitespace-nowrap text-xs font-semibold px-2.5 py-1 rounded-full ${done ? "bg-[hsl(var(--secondary))] text-white" : "bg-muted text-muted-foreground"}`}>{label}</div>
                {i < STAGES.length - 1 && <ArrowRight size={12} className="text-muted-foreground shrink-0" />}
              </React.Fragment>
            );
          })}
          {cancelled && <span className="ml-2 text-xs font-semibold px-2.5 py-1 rounded-full bg-destructive text-white">Cancelled</span>}
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-2">
          {/* Customer */}
          <div className="bg-muted rounded-lg p-4">
            <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Customer</div>
            <div className="flex items-center gap-2 text-sm font-semibold"><User size={14} /> {o.customer_name || "—"}</div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1"><Phone size={14} /> {o.customer_phone || "—"}</div>
            <div className="flex items-start gap-2 text-sm text-muted-foreground mt-1"><MapPin size={14} className="mt-0.5" /> {o.address || "—"}</div>
          </div>
          {/* Payment / value */}
          <div className="bg-muted rounded-lg p-4 text-sm">
            <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Payment & Value</div>
            <Row label="Order value" value={money(o.subtotal)} />
            <Row label="RicePoints used" value={money(o.points_used)} accent />
            <Row label="Payable" value={money(o.payable)} bold />
            <Row label="Payment" value={`${(o.payment_method || "").toUpperCase()} · ${o.payment_status}`} />
            <Row label="Remaining RicePoints" value={money(o.remaining_balance)} />
          </div>
          {/* Products */}
          <div className="bg-muted rounded-lg p-4">
            <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Products</div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {o.items.map((it) => (
                <div key={it.product_id} className="flex justify-between text-sm">
                  <span className="truncate pr-2">{it.name} <span className="text-muted-foreground">×{it.quantity}</span></span>
                  <span className="font-mono-points">{money(it.line_total)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Order lifecycle actions */}
        {!cancelled && !refunded && o.next_status && (
          <div className="mt-4 flex flex-wrap items-end gap-3">
            {o.fulfillment_status === "READY" ? (
              <DeliveryAssign o={o} staff={deliveryStaff} act={act} />
            ) : (
              <Button onClick={() => act(() => api.post(`/admin/orders/${o.order_id}/status`, { status: o.next_status }), `Order updated: ${o.next_action}`)} data-testid={`order-advance-${o.order_id}`}>
                {o.next_action} <ArrowRight size={16} />
              </Button>
            )}
            <Button variant="outline" className="text-destructive" data-testid={`order-cancel-${o.order_id}`}
                    onClick={() => act(() => api.post(`/admin/orders/${o.order_id}/status`, { status: "CANCELLED" }), "Order cancelled")}>
              <XCircle size={16} /> Cancel Order
            </Button>
            <RefundDialog order={o} act={act} />
          </div>
        )}
        {(o.fulfillment_status === "DELIVERED" && !refunded) && (
          <div className="mt-4"><RefundDialog order={o} act={act} /></div>
        )}
        {refunded && <div className="mt-4 text-sm font-semibold text-destructive">This order has been refunded.</div>}

        {/* RICE TRADE-IN section */}
        {o.tradein && <TradeInPanel ti={o.tradein} staff={staff} act={act} />}
      </div>
    </div>
  );
}

function DeliveryAssign({ o, staff, act }) {
  const [sid, setSid] = useState(staff[0]?.user_id || "");
  return (
    <div className="flex items-end gap-2">
      <div>
        <Label className="text-xs">Assign delivery staff</Label>
        <select value={sid} onChange={(e) => setSid(e.target.value)} className="mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid={`delivery-select-${o.order_id}`}>
          {staff.length === 0 && <option value="">No staff</option>}
          {staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
        </select>
      </div>
      <Button onClick={() => act(() => api.post(`/admin/orders/${o.order_id}/status`, { status: "OUT_FOR_DELIVERY", staff_id: sid || null }), "Order out for delivery")} data-testid={`order-assign-delivery-${o.order_id}`}>
        <Truck size={16} /> Assign & Send Out
      </Button>
    </div>
  );
}

function TradeInPanel({ ti, staff, act }) {
  const pickupStaff = staff.filter((s) => s.role === "staff");
  const [weight, setWeight] = useState(ti.actual_weight || "");
  const [reason, setReason] = useState("");
  const [pd, setPd] = useState(ti.pickup_date || "");
  const [sid, setSid] = useState(pickupStaff[0]?.user_id || "");
  const final = ["Points Credited", "Rejected", "Cancelled"].includes(ti.status);
  const tid = ti.trade_id;

  return (
    <div className="mt-5 border-2 border-dashed border-primary/30 rounded-xl p-4 bg-primary/[0.03]" data-testid={`order-tradein-${tid}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-heading font-bold"><Recycle size={18} className="text-primary" /> RICE TRADE-IN <span className="font-mono-points text-sm text-muted-foreground">#{tid}</span></div>
        {ti.is_active && !final && (
          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-primary text-primary-foreground">NEXT: {ti.next_action}</span>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-3 text-sm">
        <div><div className="text-xs text-muted-foreground">Estimated quantity</div><div className="font-mono-points font-semibold">{ti.estimated_kg} kg (~{money(ti.estimated_points)})</div></div>
        <div><div className="text-xs text-muted-foreground">Actual quantity</div><div className="font-mono-points font-semibold">{ti.actual_weight != null ? `${ti.actual_weight} kg` : "Not collected yet"}</div></div>
        <div><div className="text-xs text-muted-foreground">Status</div><div className="font-semibold">{ti.status}{ti.assigned_staff_name ? ` · ${ti.assigned_staff_name}` : ""}</div></div>
      </div>

      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Coins size={12} /> RicePoints are credited only after the actual weight is entered and the trade-in is approved (weight × points per kg).</p>

      {!final && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          {ti.status === "Requested" && (
            <Dialog>
              <DialogTrigger asChild><Button size="sm" data-testid={`ti-schedule-${tid}`}>Schedule Pickup</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle>Schedule pickup</DialogTitle><DialogDescription>Choose a pickup date for #{tid}.</DialogDescription></DialogHeader>
                <Label>Pickup date</Label><Input type="date" value={pd} onChange={(e) => setPd(e.target.value)} data-testid={`ti-schedule-date-${tid}`} />
                <DialogFooter><Button onClick={() => act(() => api.post(`/admin/tradeins/${tid}/schedule`, { pickup_date: pd, pickup_time: ti.pickup_time || "10 AM - 12 PM" }), "Pickup scheduled")}>Confirm</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {ti.status === "Scheduled" && (
            <div className="flex items-end gap-2">
              <div><Label className="text-xs">Assign pickup staff</Label>
                <select value={sid} onChange={(e) => setSid(e.target.value)} className="mt-1 h-8 rounded-md border border-input bg-background px-2 text-sm" data-testid={`ti-assign-select-${tid}`}>
                  {pickupStaff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
                </select>
              </div>
              <Button size="sm" onClick={() => act(() => api.post(`/admin/tradeins/${tid}/assign`, { staff_id: sid }), "Staff assigned")} data-testid={`ti-assign-${tid}`}>Assign Staff</Button>
            </div>
          )}
          {["Pickup Assigned", "Collected"].includes(ti.status) && (
            <div className="flex items-end gap-2">
              <div><Label className="text-xs">Actual weight (kg)</Label><Input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className="h-8 w-28" data-testid={`ti-weight-${tid}`} /></div>
              <Button size="sm" variant="secondary" onClick={() => act(() => api.post(`/staff/pickups/${tid}/collect`, { actual_weight: parseFloat(weight) }), "Weight recorded")} data-testid={`ti-collect-${tid}`}>Mark Collected & Enter Weight</Button>
            </div>
          )}
          {ti.status === "Weighed" && (
            <Button size="sm" onClick={() => act(() => api.post(`/admin/tradeins/${tid}/accept`), "Trade-in approved")} data-testid={`ti-approve-${tid}`}><Check size={15} /> Approve Trade-In</Button>
          )}
          {ti.status === "Accepted" && (
            <Button size="sm" onClick={() => act(() => api.post(`/admin/tradeins/${tid}/credit`), "RicePoints credited")} data-testid={`ti-credit-${tid}`}>
              <Coins size={15} /> Credit RicePoints (₹{Math.round((ti.actual_weight || 0) * 10)})
            </Button>
          )}
          <Dialog>
            <DialogTrigger asChild><Button size="sm" variant="outline" className="text-destructive" data-testid={`ti-reject-btn-${tid}`}>Reject Trade-In</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Reject trade-in</DialogTitle><DialogDescription>Provide a reason for #{tid}. No points will be credited.</DialogDescription></DialogHeader>
              <Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. rice not eligible" data-testid={`ti-reason-${tid}`} />
              <DialogFooter><Button variant="destructive" onClick={() => act(() => api.post(`/admin/tradeins/${tid}/reject`, { reason }), "Trade-in rejected")}>Confirm Reject</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      {ti.status === "Points Credited" && <div className="mt-3 text-sm font-semibold text-[hsl(var(--secondary))]">✓ Credited {money(ti.points_credited)} RicePoints</div>}
      {ti.status === "Rejected" && <div className="mt-3 text-sm font-semibold text-destructive">Trade-in rejected — no points credited.</div>}
    </div>
  );
}

function RefundDialog({ order, act }) {
  const [amount, setAmount] = useState("");
  const [open, setOpen] = useState(false);
  const doRefund = () => act(async () => {
    const body = amount ? { amount: Number(amount) } : {};
    const { data } = await api.post(`/admin/orders/${order.order_id}/refund`, body);
    setOpen(false); setAmount("");
    toast.success(`Refunded ${money(data.amount)}${data.points_reversed ? ` · ${money(data.points_reversed)} RicePoints reversed` : ""}`);
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="text-destructive" data-testid={`refund-btn-${order.order_id}`}>Refund</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Refund {order.order_id}</DialogTitle>
          <DialogDescription>Payable {money(order.payable)} · RicePoints used {money(order.points_used)}. Leave blank for full refund; enter an amount for partial (points reverse proportionally).</DialogDescription>
        </DialogHeader>
        <Label className="mt-2">Refund amount (₹)</Label>
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Full: ${order.payable}`} data-testid={`refund-amount-${order.order_id}`} />
        <DialogFooter><Button variant="destructive" onClick={doRefund} data-testid={`refund-confirm-${order.order_id}`}>Process Refund</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono-points ${bold ? "font-bold" : "font-semibold"} ${accent ? "text-primary" : ""}`}>{value}</span>
    </div>
  );
}
